import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLinkActive,
  RouterLink,
} from '@angular/router';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuService } from './app.menu.service';
import { LayoutService } from './service/app.layout.service';
import { ChatService } from '../services/chat/chat.service';
import { NgClass } from '@angular/common';
import { BadgeModule } from 'primeng/badge';
import { AlarmService } from '../services/alarm.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-menuitem]',
  template: `
    <ng-container>
      @if(root && item.visible !== false){
      <div class="layout-menuitem-root-text">
        {{ item.label }}
      </div>
      } @if(item.routerLink && !item.items && item.visible !== false){
      <a
        (click)="itemClick($event)"
        [ngClass]="item.class"
        [routerLink]="item.routerLink"
        routerLinkActive="active-route"
        [routerLinkActiveOptions]="
          item.routerLinkActiveOptions || {
            paths: 'exact',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored'
          }
        "
        [fragment]="item.fragment"
        [queryParamsHandling]="item.queryParamsHandling"
        [preserveFragment]="item.preserveFragment"
        [skipLocationChange]="item.skipLocationChange"
        [replaceUrl]="item.replaceUrl"
        [state]="item.state"
        [queryParams]="item.queryParams"
        [attr.target]="item.target"
        tabindex="0"
      >
        @if(item.label==="Συνομιλίες" ){
        @if(chatService.newSessionsStarted()>0){
        <i [ngClass]="item.icon" class="ml layout-menuitem-icon"></i>
        <span class="layout-menuitem-text mr-2">{{ item.label }}</span>
        <p-badge [value]="chatService.newSessionsStarted()" />

        }@else{
        <i [ngClass]="item.icon" class="ml layout-menuitem-icon"></i>
        <span class="layout-menuitem-text">{{ item.label }}</span>
        @if(item.items){
        <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
        } } } @else if(item.label==="Διαχείριση Αρχείων" ){
        @if(alarmService.filesCounter()>0){
        <i [ngClass]="item.icon" class="ml layout-menuitem-icon"></i>
        <span class="layout-menuitem-text mr-2">{{ item.label }}</span>
        <p-badge [value]="alarmService.filesCounter()" />

        } @else{
        <i [ngClass]="item.icon" class="ml layout-menuitem-icon"></i>
        <span class="layout-menuitem-text">{{ item.label }}</span>
        @if(item.items){
        <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
        } } } @else if(item.label==="Επείγουσες Ειδοποίησεις" ){
        @if(alarmService.emergencyAlertCounter()>0){
        <i [ngClass]="item.icon" class="ml layout-menuitem-icon"></i>
        <span class="layout-menuitem-text mr-2">{{ item.label }}</span>
        <p-badge [value]="alarmService.emergencyAlertCounter()" />

        } @else{
        <i [ngClass]="item.icon" class="ml layout-menuitem-icon"></i>
        <span class="layout-menuitem-text">{{ item.label }}</span>
        @if(item.items){
        <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
        } } } @else{

        <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
        <span class="layout-menuitem-text">{{ item.label }}</span>
        @if(item.items){
        <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
        } }
      </a>
      } @if(item.items && item.visible !== false){
      <ul [@children]="submenuAnimation">
        @for (child of item.items; track child; let i = $index) {
        <li
          app-menuitem
          [item]="child"
          [index]="i"
          [parentKey]="key"
          [class]="child.badgeClass"
        ></li>
        }
      </ul>
      }
    </ng-container>
  `,
  animations: [
    trigger('children', [
      state(
        'collapsed',
        style({
          height: '0',
        })
      ),
      state(
        'expanded',
        style({
          height: '*',
        })
      ),
      transition(
        'collapsed <=> expanded',
        animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')
      ),
    ]),
  ],
  standalone: true,
  imports: [RouterLinkActive, NgClass, RouterLink, BadgeModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppMenuitemComponent implements OnInit, OnDestroy {
  layoutService = inject(LayoutService);
  router = inject(Router);
  private menuService = inject(MenuService);

  @Input() item: any;

  @Input() index!: number;

  @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

  @Input() parentKey!: string;

  active = false;

  menuSourceSubscription: Subscription;

  menuResetSubscription: Subscription;

  key: string = '';

  public chatService = inject(ChatService);
  public alarmService = inject(AlarmService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.menuSourceSubscription = this.menuService.menuSource$.subscribe(
      (value) => {
        Promise.resolve(null).then(() => {
          if (value.routeEvent) {
            this.active =
              value.key === this.key || value.key.startsWith(this.key + '-')
                ? true
                : false;
          } else {
            if (
              value.key !== this.key &&
              !value.key.startsWith(this.key + '-')
            ) {
              this.active = false;
            }
          }
        });
      }
    );

    this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
      this.active = false;
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((params) => {
        if (this.item.routerLink) {
          this.updateActiveStateFromRoute();
        }
      });
  }

  ngOnInit() {
    this.key = this.parentKey
      ? this.parentKey + '-' + this.index
      : String(this.index);

    if (this.item.routerLink) {
      this.updateActiveStateFromRoute();
    }
  }

  updateActiveStateFromRoute() {
    let activeRoute = this.router.isActive(this.item.routerLink[0], {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored',
    });

    if (activeRoute) {
      this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
    }
  }

  itemClick(event: Event) {
    if (this.item.disabled) {
      event.preventDefault();
      return;
    }

    if (this.item.command) {
      this.item.command({ originalEvent: event, item: this.item });
    }

    if (this.item.items) {
      this.active = !this.active;
    }
    const menuItem = (event.target as HTMLSpanElement).innerHTML;

    this.menuService.onMenuStateChange({ key: this.key });
  }

  get submenuAnimation() {
    return this.root ? 'expanded' : this.active ? 'expanded' : 'collapsed';
  }

  @HostBinding('class.active-menuitem')
  get activeClass() {
    return this.active && !this.root;
  }

  ngOnDestroy() {
    if (this.menuSourceSubscription) {
      this.menuSourceSubscription.unsubscribe();
    }

    if (this.menuResetSubscription) {
      this.menuResetSubscription.unsubscribe();
    }
  }
}
